/**
 * Resilient Auth Token Storage
 * 
 * Stores the auth token in FOUR locations for maximum resilience:
 * 1. localStorage (fastest, but wiped on fitnessand.me)
 * 2. sessionStorage (survives refresh within same tab, independent of localStorage)
 * 3. First-party cookie (survives across tabs and sessions)
 * 4. IndexedDB (completely separate storage engine)
 * 
 * Read priority: localStorage -> sessionStorage -> cookie -> IndexedDB
 * Write: always write to all four
 * Clear: always clear all four
 */

const TOKEN_KEY = 'auth_token';
const SESSION_TOKEN_KEY = 'fb_auth_session';
const COOKIE_NAME = 'fb_auth_token';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days
const IDB_NAME = 'fb_auth_db';
const IDB_STORE = 'tokens';

// ─── Cookie helpers ───────────────────────────────────────────

function setCookie(name: string, value: string, maxAge: number): void {
  const isSecure = window.location.protocol === 'https:';
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `path=/`,
    `max-age=${maxAge}`,
    `SameSite=Lax`,
  ];
  if (isSecure) parts.push('Secure');
  document.cookie = parts.join('; ');
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; path=/; max-age=0`;
}

// ─── IndexedDB helpers (async) ────────────────────────────────

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key: string, value: string): Promise<void> {
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(value, key);
    await new Promise<void>((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(); });
    db.close();
  } catch { /* noop */ }
}

async function idbGet(key: string): Promise<string | null> {
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(key);
    const result = await new Promise<string | null>((res, rej) => {
      req.onsuccess = () => res(req.result || null);
      req.onerror = () => rej();
    });
    db.close();
    return result;
  } catch { return null; }
}

async function idbDelete(key: string): Promise<void> {
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(key);
    await new Promise<void>((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(); });
    db.close();
  } catch { /* noop */ }
}

// ─── Public API ───────────────────────────────────────────────

export function saveToken(token: string): void {
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* noop */ }
  try { sessionStorage.setItem(SESSION_TOKEN_KEY, token); } catch { /* noop */ }
  setCookie(COOKIE_NAME, token, COOKIE_MAX_AGE);
  idbSet(TOKEN_KEY, token); // fire-and-forget
  console.log('[TokenStorage] Token saved to all 4 stores');
}

/** Synchronous read from localStorage / sessionStorage / cookie.
 *  Also kicks off an async IDB check to recover the token later if needed. */
export function getToken(): string | null {
  // 1. localStorage
  try {
    const fromLS = localStorage.getItem(TOKEN_KEY);
    if (fromLS) return fromLS;
  } catch { /* noop */ }

  // 2. sessionStorage
  try {
    const fromSS = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (fromSS) {
      try { localStorage.setItem(TOKEN_KEY, fromSS); } catch { /* noop */ }
      console.log('[TokenStorage] RECOVERED from sessionStorage');
      return fromSS;
    }
  } catch { /* noop */ }

  // 3. Cookie
  const fromCookie = getCookie(COOKIE_NAME);
  if (fromCookie) {
    try { localStorage.setItem(TOKEN_KEY, fromCookie); } catch { /* noop */ }
    try { sessionStorage.setItem(SESSION_TOKEN_KEY, fromCookie); } catch { /* noop */ }
    console.log('[TokenStorage] RECOVERED from cookie');
    return fromCookie;
  }

  return null;
}

/** Async recovery from IndexedDB — call this on app startup as a last resort */
export async function recoverTokenFromIDB(): Promise<string | null> {
  const token = await idbGet(TOKEN_KEY);
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* noop */ }
    try { sessionStorage.setItem(SESSION_TOKEN_KEY, token); } catch { /* noop */ }
    setCookie(COOKIE_NAME, token, COOKIE_MAX_AGE);
    console.log('[TokenStorage] RECOVERED from IndexedDB');
  }
  return token;
}

export function clearToken(): void {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
  try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* noop */ }
  deleteCookie(COOKIE_NAME);
  idbDelete(TOKEN_KEY); // fire-and-forget
}

// ─── Diagnostic: call on mount to log storage state ───────────

export function logStorageState(): void {
  const ls = (() => { try { return !!localStorage.getItem(TOKEN_KEY); } catch { return false; } })();
  const ss = (() => { try { return !!sessionStorage.getItem(SESSION_TOKEN_KEY); } catch { return false; } })();
  const ck = !!getCookie(COOKIE_NAME);
  console.log(`[TokenStorage] State → localStorage:${ls} sessionStorage:${ss} cookie:${ck}`);
}
